import { useQuery } from "@apollo/client";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listProducts } from "../graphql/queries/listProducts";

const DEBOUNCE_MS = 400;

export const useProducts = () => {
  const [filterData, setFilterData] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});

  const debouncedSetFilters = useRef(
    debounce((val) => setDebouncedFilters(val), DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    debouncedSetFilters(filterData);
  }, [filterData, debouncedSetFilters]);

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  // Build GraphQL filters from name and sku
  const filters = useMemo(() => {
    const { name, sku } = debouncedFilters;
    const metadataFilters = [];
    if (name?.trim()) metadataFilters.push({ path: "name", value: name.trim() });
    if (sku?.trim()) metadataFilters.push({ path: "sku", value: sku.trim() });

    const hasMetadata = metadataFilters.length > 0;
    if (!hasMetadata) return undefined;

    return { metadataFilters };
  }, [debouncedFilters]);

  const { data, loading, error, refetch } = useQuery(listProducts, {
    variables: { filters },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const allProducts = useMemo(() => {
    if (!data?.listKeywordGroups) return [];

    return data.listKeywordGroups
      .map((group) => {
        const meta =
          typeof group.metadata === "string"
            ? JSON.parse(group.metadata)
            : group.metadata || {};
        return {
          id: group.id,
          name: group.name,
          slug: group.slug,
          type: group.type,
          deleted: group.deleted,
          imageUrl: group.imageUrl,
          keywords: group.keywords,
          keywordsGroupIds: group.keywordsGroupIds,
          category: group.category,
          metadata: meta,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // Client-side filter by product group ID (matches keywordsGroupIds)
  const products = useMemo(() => {
    const selectedGroupId = debouncedFilters.productGroupId;
    if (!selectedGroupId) return allProducts;
    return allProducts.filter((product) =>
      (product.keywordsGroupIds || []).includes(selectedGroupId)
    );
  }, [allProducts, debouncedFilters.productGroupId]);

  const handleFilterChange = useCallback((newFilterData) => {
    setFilterData(newFilterData);
  }, []);

  return {
    products,
    allProducts,
    loading,
    error,
    filterData,
    refetch,
    handlers: {
      handleFilterChange,
    },
  };
};
